import React, { useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarDays,
  faCity,
  faGift,
  faLandmark,
  faMagnifyingGlass,
  faMusic,
  faPencil,
  faScroll,
  faTicket,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import NewsletterSignUp from '../../common/Newsletter-sign';
import Footer from '../../Footer/Footer';
import { useBlackFriday } from '../../../contexts/BlackFridayContext';

const heroImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvoir%20Castle%20Christmas-28.webp';
const grottoImage = 'https://BelvoirCastle.b-cdn.net/Christmas/Belvoir%20Castle%20Christmas-91.webp';

// Reveal a town by setting its name. Leave name blank until that week is announced.
const JOURNEY_STOPS = [
  { label: 'Week 1', name: '' },
  { label: 'Week 2', name: '' },
  { label: 'Week 3', name: '' },
  { label: 'Week 4', name: '' },
  { label: 'Week 5', name: '' },
  { label: 'The trail', name: 'Belvoir Castle', atCastle: true },
];

const C = {
  navy:      '#101d40',
  midnight:  '#0A1428',
  gold:      '#C9963C',
  goldDk:    '#A67C2A',
  goldLt:    '#F5E8C8',
  bronze:    '#8B5E3C',
  crimson:   '#8B1A1A',
  ivory:     '#FAF7F0',
  parchment: '#F3EDD8',
  muted:     'rgba(28,28,30,0.62)',
  border:    'rgba(28,28,30,0.08)',
};

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  @media (max-width: 768px) { padding-top: clamp(188px, 34vw, 228px); }
`;

const HeroWrap = styled.section`
  position: relative;
  width: 100%;
  min-height: clamp(560px, 78vh, 860px);
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  @media (max-width: 768px) {
    min-height: 0;
    align-items: stretch;
  }
`;

const HeroImg = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 36% center;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(90deg,
      rgba(10,20,40,0.9) 0%,
      rgba(10,20,40,0.78) 28%,
      rgba(10,20,40,0.42) 52%,
      rgba(10,20,40,0.12) 100%
    ),
    linear-gradient(to top,
      rgba(10,20,40,0.72) 0%,
      rgba(10,20,40,0.28) 48%,
      rgba(10,20,40,0.12) 100%
    );
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: clamp(2rem, 5vw, 3.5rem) clamp(1.25rem, 5vw, 3.5rem);
  color: #fff;
  min-width: 0;

  @media (max-width: 768px) {
    padding-top: 1.5rem;
    padding-bottom: 4.5rem;

    > a {
      display: block;
      width: 100%;
      box-sizing: border-box;
      text-align: center;
      white-space: normal;
    }
  }
`;

const HeroKicker = styled.p`
  margin: 0 0 0.65rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};

  @media (max-width: 768px) {
    letter-spacing: 0.08em;
    line-height: 1.45;
  }
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.65rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 6.5vw, 5rem);
  font-weight: 700;
  line-height: 1.05;
  color: #fff;
  em { font-style: italic; color: ${C.gold}; }
`;

const HeroStrap = styled.p`
  margin: 0 0 1.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.4rem);
  font-style: italic;
  font-weight: 300;
  color: rgba(255,255,255,0.82);
  max-width: 38rem;
`;

const BadgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem 0.65rem;
  margin-bottom: 1.75rem;
`;

const Pill = styled.span`
  display: inline-block;
  padding: 0.32rem 0.85rem;
  border-radius: 999px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${p => p.$fill ? C.gold : 'rgba(255,255,255,0.11)'};
  color: #fff;
  border: 1px solid ${p => p.$fill ? 'transparent' : 'rgba(255,255,255,0.26)'};
`;

const HeroBtns = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
    a { width: 100%; text-align: center; }
  }
`;

const BtnPrimary = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2.25rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;

  @media (max-width: 768px) {
    box-sizing: border-box;
    white-space: normal;
    letter-spacing: 0.06em;
    padding: 1rem 1.1rem;
  }
`;

const BtnGhost = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.65);
  background: rgba(255,255,255,0.07);
  color: #fff;
  text-decoration: none;

  @media (max-width: 768px) {
    box-sizing: border-box;
    white-space: normal;
    letter-spacing: 0.06em;
    padding: 1rem 1.1rem;
  }
`;

const Section = styled.section`
  position: relative;
  overflow: hidden;
  padding: clamp(3.5rem, 7vw, 5.5rem) clamp(1.25rem, 5vw, 3.5rem);
  background: ${p => p.$bg || 'transparent'};
  border-bottom: ${p => p.$border ? `1px solid ${C.border}` : 'none'};
`;

const Inner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 1280px;
  margin: 0 auto;
`;

const PrintField = styled.div`
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  color: ${p => (p.$light ? 'rgba(255,255,255,0.72)' : C.gold)};
  transform: translateY(${p => p.$shift || 0}px);

  svg {
    position: absolute;
    fill: currentColor;
  }

  @media (max-width: 900px) {
    svg.edge { display: none; }
  }

  @media (prefers-reduced-motion: reduce) {
    transform: none;
  }
`;

const PRINT_SETS = [
  [
    { top: '1.1rem', right: '1.1rem', rotate: 18, size: 18, corner: true },
    { top: '2.4rem', right: '2.6rem', rotate: -12, size: 13, corner: true },
    { top: '18%', left: '1.4rem', rotate: -24, size: 22 },
    { top: '34%', left: '2.2rem', rotate: 8, size: 16 },
    { top: '58%', right: '1.6rem', rotate: 22, size: 20 },
    { top: '74%', right: '2.8rem', rotate: -6, size: 15 },
  ],
  [
    { top: '1.1rem', left: '1.1rem', rotate: -20, size: 18, corner: true },
    { top: '2.5rem', left: '2.7rem', rotate: 10, size: 13, corner: true },
    { top: '22%', right: '1.5rem', rotate: 16, size: 22 },
    { top: '40%', right: '2.4rem', rotate: -14, size: 16 },
    { top: '66%', left: '1.6rem', rotate: -8, size: 20 },
    { top: '80%', left: '2.6rem', rotate: 18, size: 14 },
  ],
  [
    { top: '1.2rem', right: '1.2rem', rotate: 24, size: 16, corner: true },
    { top: '2.6rem', right: '2.8rem', rotate: -8, size: 12, corner: true },
    { top: '16%', left: '1.8rem', rotate: -18, size: 20 },
    { top: '46%', left: '1.2rem', rotate: 12, size: 15 },
    { top: '62%', right: '1.4rem', rotate: -22, size: 22 },
    { top: '78%', right: '2.5rem', rotate: 6, size: 16 },
  ],
  [
    { top: '22%', right: '8%', rotate: -18, size: 26, corner: true },
    { top: '30%', right: '14%', rotate: 8, size: 18, corner: true },
    { top: '42%', right: '6%', rotate: 22, size: 22, corner: true },
    { top: '54%', right: '16%', rotate: -6, size: 16, corner: true },
    { top: '66%', right: '9%', rotate: 14, size: 20, corner: true },
  ],
];

function MousePrints({ variant = 0, light = false, shift = 0 }) {
  const marks = PRINT_SETS[variant % PRINT_SETS.length];
  return (
    <PrintField $light={light} $shift={shift} aria-hidden="true">
      {marks.map((mark, index) => (
        <svg
          key={index}
          className={mark.corner ? 'corner' : 'edge'}
          viewBox="0 0 40 40"
          style={{
            top: mark.top,
            left: mark.left,
            right: mark.right,
            width: mark.size,
            height: mark.size,
            transform: `rotate(${mark.rotate}deg)`,
          }}
        >
          <ellipse cx="20" cy="26" rx="8" ry="6.5" />
          <circle cx="10" cy="14" r="3" />
          <circle cx="16.2" cy="9" r="2.5" />
          <circle cx="23.8" cy="9" r="2.5" />
          <circle cx="30" cy="14" r="3" />
        </svg>
      ))}
    </PrintField>
  );
}

const Kicker = styled.p`
  margin: 0 0 0.6rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${C.gold};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${C.gold};
  margin-bottom: clamp(1.1rem, 2.5vw, 1.6rem);
`;

const Timeline = styled.ol`
  list-style: none;
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  margin: 0.35rem 0 0;
  padding: 0;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    top: 7px;
    left: calc(100% / 12);
    right: calc(100% / 12);
    height: 2px;
    background: rgba(16, 29, 64, 0.14);
  }

  @media (max-width: 760px) {
    display: flex;
    min-width: 0;
    max-width: 100%;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    padding-bottom: 0.4rem;
    &::before { display: none; }
  }
`;

const TimelineStop = styled.li`
  position: relative;
  text-align: center;
  min-width: 0;
  padding: 0 0.35rem;

  @media (max-width: 760px) {
    flex: 0 0 9.5rem;
    scroll-snap-align: center;

    &:not(:last-child)::after {
      content: '';
      position: absolute;
      top: 7px;
      left: 50%;
      width: 100%;
      height: 2px;
      background: rgba(16, 29, 64, 0.14);
    }
  }
`;

const dotPulse = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 rgba(201, 150, 60, 0.5); }
  70% { box-shadow: 0 0 0 8px rgba(201, 150, 60, 0); }
`;

const nameFlip = keyframes`
  from { transform: rotateX(72deg); opacity: 0; }
  to { transform: rotateX(0); opacity: 1; }
`;

const lineIn = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const cardIn = keyframes`
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: translateY(0); }
`;

const StopDot = styled.span`
  position: relative;
  z-index: 1;
  display: block;
  width: 16px;
  height: 16px;
  margin: 0 auto;
  border-radius: 50%;
  background: ${p => (p.$revealed || p.$current ? C.gold : C.ivory)};
  border: 2px solid ${p => (p.$revealed || p.$current ? C.gold : 'rgba(16, 29, 64, 0.28)')};
  animation: ${p => (p.$current ? dotPulse : 'none')} 1.8s ease-out infinite;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const TrailWalker = styled.div`
  display: none;
  pointer-events: none;

  @media (min-width: 761px) {
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 22px;
    z-index: 2;
  }

  @media (prefers-reduced-motion: reduce) {
    display: none;
  }
`;

const printWalk = keyframes`
  0% { left: 8.333%; opacity: 0; }
  8% { opacity: 0.9; }
  92% { opacity: 0.9; }
  100% { left: 91.667%; opacity: 0; }
`;

const WalkerPrint = styled.span`
  position: absolute;
  top: -1px;
  width: 13px;
  height: 13px;
  margin-left: -6px;
  color: ${C.gold};
  animation: ${printWalk} 18s linear infinite;
  animation-delay: ${p => p.$step * -6}s;

  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
  }
`;

const WalkerMark = styled.span`
  position: absolute;
  top: ${p => (p.$mouse ? '-16px' : '-1px')};
  left: ${p => p.$left}%;
  width: ${p => (p.$mouse ? '36px' : '13px')};
  height: ${p => (p.$mouse ? '24px' : '13px')};
  margin-left: ${p => (p.$mouse ? '-18px' : '-6px')};
  color: ${p => (p.$mouse ? C.navy : C.gold)};

  svg {
    width: 100%;
    height: 100%;
    fill: currentColor;
  }
`;

const StopLabel = styled.p`
  margin: 0.75rem 0 0.2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: rgba(16, 29, 64, 0.45);
`;

const StopName = styled.p`
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(0.95rem, 1.5vw, 1.15rem);
  font-weight: 700;
  line-height: 1.25;
  color: ${p => (p.$hidden ? 'rgba(16, 29, 64, 0.4)' : C.navy)};
  font-style: ${p => (p.$hidden ? 'italic' : 'normal')};
  transform-origin: center top;
  animation: ${p => (p.$hidden ? 'none' : nameFlip)} 0.55s ease;

  svg {
    width: 0.8rem;
    height: 0.8rem;
    color: ${C.gold};
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.8rem, 4.2vw, 2.85rem);
  font-weight: 700;
  line-height: 1.1;
  color: ${p => p.$light ? '#fff' : C.navy};
  em { font-style: italic; color: ${C.gold}; }
`;

const SectionLead = styled.p`
  max-width: 640px;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  margin: 0 0 clamp(1.5rem, 3vw, 2.25rem);
`;

const TwoCols = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1fr 1fr'};
  gap: clamp(2rem, 5vw, 4rem);
  align-items: ${p => p.$align || 'start'};
  @media (max-width: 860px) { grid-template-columns: 1fr; }
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: clamp(1rem, 2vw, 1.4rem);
`;

const FeatureCard = styled.div`
  background: ${p => p.$dark ? C.navy : '#fff'};
  border-radius: 14px;
  padding: clamp(1.5rem, 3vw, 2rem);
  border: 1px solid ${p => p.$dark ? 'transparent' : C.border};
  box-shadow: 0 4px 20px rgba(16,29,64,0.06);
  opacity: ${p => (p.$delay == null ? 1 : 0)};
  animation: ${p => (p.$delay != null && p.$shown ? cardIn : 'none')} 0.65s ease both;
  animation-delay: ${p => p.$delay || 0}s;

  @media (prefers-reduced-motion: reduce) {
    opacity: 1;
    animation: none;
  }
`;

const FeatureIcon = styled.div`
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(201,150,60,0.12);
  border-radius: 10px;
  margin-bottom: 1rem;
  color: ${C.gold};
  font-size: 1.15rem;

  svg {
    width: 1.15rem;
    height: 1.15rem;
  }

  svg[viewBox='0 0 96 64'] {
    width: 1.55rem;
    height: auto;
  }
`;

const FeatureTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 700;
  color: ${p => p.$light ? '#fff' : C.navy};
`;

const FeatureBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  line-height: 1.65;
  color: ${p => p.$light ? 'rgba(255,255,255,0.65)' : C.muted};
`;

const StepGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2px;
  border-radius: 12px;
  overflow: hidden;
  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const Step = styled.div`
  background: ${p => p.$bg};
  padding: clamp(1.25rem, 2.5vw, 1.75rem);
  color: #fff;
`;

const StepNum = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 2.2rem;
  font-weight: 700;
  color: rgba(255,255,255,0.22);
  line-height: 1;
  margin-bottom: 0.7rem;
`;

const StepTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: #fff;
`;

const StepBody = styled.p`
  margin: 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  line-height: 1.6;
  color: rgba(255,255,255,0.7);
`;

const InfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(185px, 1fr));
  gap: 1px;
  background: ${C.border};
  border-radius: 10px;
  overflow: hidden;
`;

const InfoItem = styled.div`
  background: #fff;
  padding: clamp(1.2rem, 2.5vw, 1.65rem);
`;

const InfoLabel = styled.div`
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.62rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: rgba(16,29,64,0.38);
  margin-bottom: 0.35rem;
`;

const InfoValue = styled.div`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.15rem;
  font-weight: 700;
  color: ${C.navy};
  line-height: 1.25;
`;

const ImgStrip = styled.div`
  position: relative;
  border-radius: 14px;
  overflow: hidden;
  height: clamp(280px, 40vw, 420px);
  img {
    position: absolute;
    width: 168%;
    height: auto;
    max-width: none;
    left: -46%;
    top: 54%;
    transform: translateY(-62%);
    object-fit: fill;
    display: block;
  }
`;

const MidCta = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 1rem 1.5rem;
  margin-top: clamp(2rem, 4vw, 2.75rem);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;
  }
  padding: clamp(1.25rem, 3vw, 1.75rem) clamp(1.5rem, 4vw, 2rem);
  border-radius: 12px;
  background: ${C.parchment};
  border: 1px solid rgba(201,150,60,0.2);
`;

const MidCtaText = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.05rem, 2.2vw, 1.28rem);
  font-weight: 600;
  line-height: 1.35;
  color: ${C.navy};
  flex: 1;
  min-width: min(100%, 16rem);

  @media (max-width: 640px) {
    flex: none;
    min-width: 0;
  }
`;

const MidCtaBtn = styled.a`
  flex-shrink: 0;
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 0.9rem 1.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;

  @media (max-width: 640px) {
    flex-shrink: 1;
    width: 100%;
    box-sizing: border-box;
    text-align: center;
    white-space: normal;
    letter-spacing: 0.06em;
    padding: 0.9rem 1rem;
  }
`;

const HuntQuote = styled.p`
  margin: 0.75rem 0 0;
  text-align: center;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.2rem, 2.4vw, 1.5rem);
  font-style: italic;
  font-weight: 600;
  line-height: 1.35;
  color: ${C.navy};
`;

const StoryBeat = styled.p`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.25rem, 2.5vw, 1.55rem);
  font-style: italic;
  font-weight: 600;
  line-height: 1.3;
  color: ${C.navy};
  opacity: ${p => (p.$motion ? 0 : 1)};
  animation: ${p => (p.$motion && p.$shown ? lineIn : 'none')} 0.7s ease both;
`;

const StoryCopy = styled.p`
  max-width: 720px;
  margin: 0 0 1rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  line-height: 1.7;
  color: ${C.muted};
  opacity: ${p => (p.$motion ? 0 : 1)};
  animation: ${p => (p.$motion && p.$shown ? lineIn : 'none')} 0.7s ease both;
  animation-delay: ${p => p.$delay || 0}s;

  &:last-of-type {
    margin-bottom: clamp(1.75rem, 3vw, 2.5rem);
  }
`;

const StoryBlock = styled.div`
  position: relative;
`;

const StoryPrints = styled.div`
  display: none;
  pointer-events: none;
  color: ${C.gold};

  @media (min-width: 860px) {
    display: block;
    position: absolute;
    top: 0.25rem;
    right: 0;
    width: 72px;
    height: 64px;
    opacity: ${p => (p.$shown ? 0.4 : 0)};
    transform: ${p => (p.$shown ? 'translate(0, 0)' : 'translate(18px, 12px)')};
    transition: opacity 0.8s ease 0.35s, transform 0.9s ease 0.15s;
  }

  svg {
    position: absolute;
    fill: currentColor;
  }

  @media (prefers-reduced-motion: reduce) {
    opacity: 0.4;
    transform: none;
    transition: none;
  }
`;

const CtaSection = styled.section`
  position: relative;
  overflow: hidden;
  background: ${C.midnight};
  padding: clamp(4rem, 8vw, 6rem) clamp(1.25rem, 5vw, 3.5rem);
  text-align: center;
`;

const CtaInner = styled.div`
  position: relative;
  z-index: 1;
  max-width: 640px;
  margin: 0 auto;
`;

const CtaTitle = styled.h2`
  margin: 0 0 1rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 5vw, 3.5rem);
  font-weight: 700;
  color: #fff;
  line-height: 1.08;
  em { font-style: italic; color: ${C.gold}; }
`;

const CtaBody = styled.p`
  margin: 0 0 2rem;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: clamp(0.95rem, 1.8vw, 1.05rem);
  color: rgba(255,255,255,0.62);
  line-height: 1.65;
`;

const CtaBtnRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  justify-content: center;

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: stretch;

    a {
      width: 100%;
      box-sizing: border-box;
      text-align: center;
    }
  }
`;

const CtaBtn = styled.a`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.88rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  padding: 1.15rem 2.75rem;
  border-radius: 999px;
  background: ${C.gold};
  color: #fff;
  text-decoration: none;
`;

const CtaSecondary = styled(Link)`
  display: inline-block;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  padding: 1.1rem 2rem;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.5);
  background: transparent;
  color: #fff;
  text-decoration: none;
`;

const CtaMeta = styled.p`
  margin: 1.25rem 0 0;
  font-family: 'Inter', -apple-system, sans-serif;
  font-size: 0.75rem;
  color: rgba(255,255,255,0.32);
  letter-spacing: 0.06em;
`;

function PawSvg() {
  return (
    <svg viewBox="0 0 40 40" aria-hidden="true">
      <ellipse cx="20" cy="26" rx="8" ry="6.5" />
      <circle cx="10" cy="14" r="3" />
      <circle cx="16.2" cy="9" r="2.5" />
      <circle cx="23.8" cy="9" r="2.5" />
      <circle cx="30" cy="14" r="3" />
    </svg>
  );
}

function MouseSvg() {
  return (
    <svg viewBox="0 0 96 64" aria-hidden="true">
      <path fill="currentColor" d="M34 40C20 42 8 34 11 22c1-5 7-6 8-1 1 6 8 11 17 12l-2 7z" />
      <ellipse cx="46" cy="40" rx="20" ry="13" fill="currentColor" />
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M50 34a16 12 0 1 0 32 0 16 12 0 1 0-32 0zm22-2.2a2.3 2.3 0 1 0 .01 0z"
      />
      <ellipse cx="84" cy="36" rx="5" ry="3.4" fill="currentColor" />
      <path
        fill="currentColor"
        fillRule="evenodd"
        d="M49 18a9 9 0 1 0 18 0 9 9 0 1 0-18 0zm4.6 0a4.4 4.4 0 1 1 8.8 0 4.4 4.4 0 1 1-8.8 0z"
      />
    </svg>
  );
}

function stopCenter(index) {
  return ((index + 0.5) / JOURNEY_STOPS.length) * 100;
}

function useInView() {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);
  const [motion, setMotion] = useState(false);

  useEffect(() => {
    const node = ref.current;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setMotion(!reduced);
    if (!node || reduced || !('IntersectionObserver' in window)) {
      setShown(true);
      return undefined;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShown(true);
        observer.disconnect();
      }
    }, { threshold: 0.28 });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return [ref, shown, motion];
}

function useTimelineTravel() {
  const ref = useRef(null);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setProgress(1);
      return undefined;
    }
    let frame = 0;
    const update = () => {
      frame = 0;
      const node = ref.current;
      if (!node) return;
      const start = window.innerHeight * 0.92;
      const end = window.innerHeight * 0.36;
      const value = (start - node.getBoundingClientRect().top) / (start - end);
      setProgress(Math.min(1, Math.max(0, value)));
    };
    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    window.addEventListener('resize', requestUpdate);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
      window.removeEventListener('resize', requestUpdate);
    };
  }, []);

  return [ref, progress];
}

const MiceTrail = () => {
  const { isActive: isBlackFriday } = useBlackFriday();
  const pageClassName = isBlackFriday ? 'christmas-page christmas-page--black-friday' : 'christmas-page';
  const [heroShift, setHeroShift] = useState(0);
  const [timelineRef, travel] = useTimelineTravel();
  const [storyRef, storyShown, storyMotion] = useInView();
  const [stepsRef, stepsShown] = useInView();

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    let frame = 0;
    const onScroll = () => {
      frame = 0;
      setHeroShift(Math.min(window.scrollY * 0.08, 36));
    };
    const requestUpdate = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(onScroll);
    };
    onScroll();
    window.addEventListener('scroll', requestUpdate, { passive: true });
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', requestUpdate);
    };
  }, []);

  const townStops = JOURNEY_STOPS.filter((stop) => !stop.atCastle);
  const namedTowns = townStops.filter((stop) => stop.name).length;
  const pauseIndex = namedTowns === 0
    ? 0
    : namedTowns >= townStops.length
      ? JOURNEY_STOPS.length - 1
      : namedTowns - 1;
  const nextHidden = JOURNEY_STOPS.findIndex((stop) => !stop.atCastle && !stop.name);
  const currentIndex = nextHidden === -1 ? JOURNEY_STOPS.length - 1 : nextHidden;
  const mouseIndex = travel * pauseIndex;

  return (
    <>
    <Page className={pageClassName}>
      <Helmet>
        <title>Mice Trail | Christmas 2026 at Belvoir Castle</title>
        <meta name="description" content="The Mice Trail returns for Christmas 2026! Follow the beloved Mice through Belvoir Castle's carol-themed grand rooms. Included free with every Deck the Halls entry ticket." />
        <link rel="canonical" href="https://www.belvoircastle.com/christmas/mice-trail" />
        <meta property="og:title" content="The Mice Trail — Christmas 2026 at Belvoir Castle" />
        <meta property="og:description" content="Follow the beloved Mice through the Castle's carol-themed rooms. Included free with every Deck the Halls entry ticket." />
        <meta property="og:image" content={heroImage} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.belvoircastle.com/christmas/mice-trail" />
        <meta property="og:site_name" content="Belvoir Castle" />
        <meta property="og:locale" content="en_GB" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Mice Trail — Christmas 2026 at Belvoir Castle" />
        <meta name="twitter:description" content="Follow the beloved Mice through the Castle's carol-themed rooms. Included free with every Deck the Halls entry ticket." />
        <meta name="twitter:image" content={heroImage} />
        <meta name="twitter:site" content="@belvoircastle" />
      </Helmet>

      <HeroWrap>
        <HeroImg src={heroImage} alt="The Mice Trail at Belvoir Castle" />
        <HeroOverlay />
        <MousePrints variant={3} light shift={heroShift} />
        <HeroContent>
          <HeroKicker>Returning for Christmas 2026 · Inside Belvoir Castle</HeroKicker>
          <HeroTitle>
            The <em>Mice Trail</em>
          </HeroTitle>
          <HeroStrap>
            Follow the beloved Mice through Belvoir Castle's grand carol-themed rooms
          </HeroStrap>
          <BadgeRow>
            <Pill $fill>Included with Deck the Halls Entry</Pill>
            <Pill>Deck the Halls Experience</Pill>
            <Pill>Family-Friendly</Pill>
          </BadgeRow>
          <HeroBtns>
            <BtnPrimary href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">
              Book Deck the Halls
            </BtnPrimary>
            <BtnGhost href="/christmas/your-festive-visit">
              Plan Your Visit
            </BtnGhost>
          </HeroBtns>
          <p style={{fontSize: "1.1rem", maxWidth: "620px", lineHeight: 1.6}}>Something unexpected has happened. The Belvoir Mice set off for the Castle and became scattered across five local towns. Follow the clues and help them find their way home for Christmas.</p>
          <BtnPrimary as={Link} to="/christmas/mouse-map" style={{ marginTop: '1rem' }}>
            Follow the mice’s journey home →
          </BtnPrimary>
        </HeroContent>
      </HeroWrap>

      <Section $bg={C.ivory} $border>
        <MousePrints variant={1} />
        <Inner>
          <Kicker>Their way home</Kicker>
          <Divider />
          <SectionTitle>Five towns, then <em>the trail</em></SectionTitle>
          <Timeline ref={timelineRef} aria-label="Mouse journey timeline">
            <TrailWalker aria-hidden="true">
              {[0, 1, 2].map((step) => (
                <WalkerPrint key={step} $step={step} style={{ transform: `rotate(${step % 2 ? 18 : -16}deg)` }}>
                  <PawSvg />
                </WalkerPrint>
              ))}
              <WalkerMark $mouse $left={stopCenter(mouseIndex)}>
                <MouseSvg />
              </WalkerMark>
            </TrailWalker>
            {JOURNEY_STOPS.map((stop, index) => {
              const revealed = stop.atCastle || Boolean(stop.name);
              return (
                <TimelineStop key={stop.label}>
                  <StopDot $revealed={revealed} $current={index === currentIndex} aria-hidden="true" />
                  <StopLabel>{stop.label}</StopLabel>
                  <StopName key={revealed ? stop.name : 'hidden'} $hidden={!revealed}>
                    {stop.atCastle && <FontAwesomeIcon icon={faLandmark} />}
                    {revealed ? stop.name : 'Hidden'}
                  </StopName>
                </TimelineStop>
              );
            })}
          </Timeline>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <MousePrints variant={2} />
        <Inner>
          <TwoCols $cols="1.1fr 0.9fr" $align="center">
            <div>
              <Kicker>The Trail</Kicker>
              <Divider />
              <SectionTitle> The Mice.<br /><em>One Magical Castle.</em></SectionTitle>
              <SectionLead>
                Our beloved Mice are back for Christmas 2026! Pick up your trail sheet on arrival and follow the route through the Castle's extraordinary seasonal rooms. How many mice can your family find? A gentle, charming adventure that weaves through one of England's most spectacular Christmas settings.
              </SectionLead>
              <SectionLead style={{ marginBottom: 0 }}>
                The trail is included in your Castle entry ticket, and don't forget to redeem your prize at the end of the trail!
              </SectionLead>
              <MidCta style={{ marginTop: '1.75rem' }}>
                <MidCtaText>Included free with every Deck the Halls entry ticket</MidCtaText>
                <MidCtaBtn href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">Book Now</MidCtaBtn>
              </MidCta>
            </div>
            <ImgStrip>
              <img src={grottoImage} alt="Child holding the Belvoir Castle Mice Trail booklet" />
            </ImgStrip>
          </TwoCols>
        </Inner>
      </Section>

      <Section $bg={C.parchment} $border>
        <MousePrints variant={0} />
        <Inner>
          <Kicker>In local towns</Kicker>
          <Divider />
          <SectionTitle>Help the Belvoir Mice <em>Find Their Way Home</em> for Christmas</SectionTitle>
          <StoryBlock ref={storyRef}>
            <StoryBeat $motion={storyMotion} $shown={storyShown}>Something unexpected has happened…</StoryBeat>
            <StoryCopy $motion={storyMotion} $shown={storyShown} $delay={0.2}>
              As the Belvoir Mice set off on their annual journey home to Belvoir Castle for Christmas, they became distracted by the twinkling lights, festive shop windows, and the excitement of the season.
            </StoryCopy>
            <StoryCopy $motion={storyMotion} $shown={storyShown} $delay={0.42}>
              Before they knew it, they had wandered off course and become scattered across five local towns.
            </StoryCopy>
            <StoryPrints $shown={storyShown} aria-hidden="true">
              <span style={{ position: 'absolute', top: 0, left: 10, width: 22, height: 22, transform: 'rotate(-16deg)' }}><PawSvg /></span>
              <span style={{ position: 'absolute', top: 24, left: 32, width: 16, height: 16, transform: 'rotate(14deg)' }}><PawSvg /></span>
              <span style={{ position: 'absolute', top: 40, left: 2, width: 18, height: 18, transform: 'rotate(8deg)' }}><PawSvg /></span>
            </StoryPrints>
          </StoryBlock>

          <Kicker>The Hunt at a Glance</Kicker>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faCity} /></FeatureIcon>
              <FeatureTitle>5 Towns</FeatureTitle>
              <FeatureBody>Five towns have been chosen as stopping points on the mice’s journey home.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faCalendarDays} /></FeatureIcon>
              <FeatureTitle>5 Weeks</FeatureTitle>
              <FeatureBody>Each week the hunt moves to a new location, bringing fresh clues and new opportunities to find a mouse.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><MouseSvg /></FeatureIcon>
              <FeatureTitle>10 Mice in Each Town</FeatureTitle>
              <FeatureBody>Ten mice will be hidden in every town, making fifty mice to discover in total.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>

          <Kicker style={{ marginTop: 'clamp(2rem, 4vw, 2.75rem)' }}>How the Adventure Works</Kicker>
          <FeatureGrid ref={stepsRef}>
            <FeatureCard $shown={stepsShown} $delay={0}>
              <FeatureIcon><MouseSvg /></FeatureIcon>
              <FeatureTitle>Follow the Clues</FeatureTitle>
              <FeatureBody>Keep an eye on Belvoir’s social media channels for weekly riddles, hints and clues revealing where the mice have been spotted.</FeatureBody>
            </FeatureCard>
            <FeatureCard $shown={stepsShown} $delay={0.12}>
              <FeatureIcon><FontAwesomeIcon icon={faMagnifyingGlass} /></FeatureIcon>
              <FeatureTitle>Find a Mouse</FeatureTitle>
              <FeatureBody>Head into town and search high and low. The mice could be almost anywhere, waiting to be found by an adventurous explorer.</FeatureBody>
            </FeatureCard>
            <FeatureCard $shown={stepsShown} $delay={0.24}>
              <FeatureIcon><FontAwesomeIcon icon={faPencil} /></FeatureIcon>
              <FeatureTitle>Give It a Name</FeatureTitle>
              <FeatureBody>Every mouse has a story still to be written. Choose a name for your new companion and make it part of the journey home.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark $shown={stepsShown} $delay={0.36}>
              <FeatureIcon><FontAwesomeIcon icon={faGift} /></FeatureIcon>
              <FeatureTitle $light>Win a Christmas Prize</FeatureTitle>
              <FeatureBody $light>Register your named mouse and you’ll be entered into a prize draw to win a festive prize from Belvoir.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>

          <SectionTitle as="h3" style={{ marginTop: 'clamp(2.25rem, 4vw, 3rem)', fontSize: 'clamp(1.7rem, 3.4vw, 2.35rem)' }}>
            Can You Help Them <em>Get Home?</em>
          </SectionTitle>
          <StoryCopy>
            As Christmas draws closer, the Belvoir Mice are hoping to make it safely back to the warmth of the Castle. Some may wander through busy market towns, others might stop to admire Christmas lights or take shelter near local landmarks, but they all share the same goal: getting home for their Mice Trail.
          </StoryCopy>
          <StoryCopy>Gather your family and become part of this magical festive adventure.</StoryCopy>
          <MidCta style={{ justifyContent: 'flex-start' }}>
            <MidCtaText style={{ flex: '0 1 auto' }}>Track their journey here</MidCtaText>
            <MidCtaBtn as={Link} to="/christmas/mouse-map">Follow the mice’s journey home</MidCtaBtn>
            <MidCtaBtn href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">Book your tickets to experience the trail</MidCtaBtn>
          </MidCta>
          <HuntQuote>“Every mouse found is one step closer to Belvoir.”</HuntQuote>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <MousePrints variant={2} />
        <Inner>
          <Kicker>What to Expect</Kicker>
          <Divider />
          <SectionTitle>Everything About <em>the Trail</em></SectionTitle>
          <FeatureGrid>
            <FeatureCard>
              <FeatureIcon><MouseSvg /></FeatureIcon>
              <FeatureTitle>The Mice</FeatureTitle>
              <FeatureBody>Our beloved Mice are tucked away throughout the Castle's grand rooms — each hiding in a different carol-themed space. Spot them all to complete the trail.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faScroll} /></FeatureIcon>
              <FeatureTitle>Trail Sheet on Arrival</FeatureTitle>
              <FeatureBody>Pick up your trail sheet when you arrive at the Castle. No extra booking, no extra cost — just collect it at the entrance and start exploring.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faMusic} /></FeatureIcon>
              <FeatureTitle>Through Deck the Halls</FeatureTitle>
              <FeatureBody>The trail takes you through every carol-themed room — from Deck the Halls to The Twelve Days of Christmas — making it the perfect way to explore the whole Castle.</FeatureBody>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FontAwesomeIcon icon={faUsers} /></FeatureIcon>
              <FeatureTitle>Perfect for All Ages</FeatureTitle>
              <FeatureBody>Designed especially for children but loved by the whole family. The trail adds a layer of discovery and fun to every room you visit.</FeatureBody>
            </FeatureCard>
            <FeatureCard $dark>
              <FeatureIcon><FontAwesomeIcon icon={faTicket} /></FeatureIcon>
              <FeatureTitle $light>Free with Entry</FeatureTitle>
              <FeatureBody $light>No separate ticket, no extra booking. The Mice Trail is included as part of every Deck the Halls entry to Belvoir Castle. Simply book your entry and the trail is yours.</FeatureBody>
            </FeatureCard>
          </FeatureGrid>
        </Inner>
      </Section>

      <Section $bg={C.parchment} $border>
        <MousePrints variant={0} />
        <Inner>
          <Kicker>How It Works</Kicker>
          <Divider />
          <SectionTitle>Three Steps to <em>the Trail</em></SectionTitle>
          <StepGrid>
            <Step $bg={C.navy}>
              <StepNum>01</StepNum>
              <StepTitle>Book Your Entry</StepTitle>
              <StepBody>Book your Deck the Halls entry to Belvoir Castle online. Use our Family Saver system to get the best price for your group — the more you add, the more you save.</StepBody>
            </Step>
            <Step $bg={C.gold}>
              <StepNum>02</StepNum>
              <StepTitle>Collect Your Trail Sheet</StepTitle>
              <StepBody>On arrival, collect your Mice Trail sheet from the Castle entrance. No separate booking required — it's included with every Deck the Halls ticket.</StepBody>
            </Step>
            <Step $bg="#1a2d5a">
              <StepNum>03</StepNum>
              <StepTitle>Explore & Spot the Mice</StepTitle>
              <StepBody>Work your way through each grand carol-themed room and find where the Three Mice are hiding. Complete the trail and enjoy a magical day at Belvoir.</StepBody>
            </Step>
          </StepGrid>
        </Inner>
      </Section>

      <Section $bg="#fff" $border>
        <MousePrints variant={2} />
        <Inner>
          <Kicker>Plan Your Visit</Kicker>
          <Divider />
          <SectionTitle>Essential <em>Information</em></SectionTitle>
          <InfoGrid style={{ marginBottom: 'clamp(2rem, 4vw, 3rem)' }}>
            <InfoItem>
              <InfoLabel>Dates</InfoLabel>
              <InfoValue>14 November –<br />30 December 2026</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Price</InfoLabel>
              <InfoValue>Included with<br />Deck the Halls <br /> Entry Ticket</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Location</InfoLabel>
              <InfoValue>Inside<br />Belvoir Castle</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Trail Sheet</InfoLabel>
              <InfoValue>Collected<br />on Arrival</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Booking</InfoLabel>
              <InfoValue>Book Castle Entry<br />— trail included</InfoValue>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Address</InfoLabel>
              <InfoValue>Belvoir Castle<br />Grantham, NG32 1PE</InfoValue>
            </InfoItem>
          </InfoGrid>
        </Inner>
      </Section>

      <div style={{ padding: '0 0 1rem' }}>
        <NewsletterSignUp tag="Christmas 2026" extraTags={['Mice Trail']} />
      </div>

      <CtaSection>
        <MousePrints variant={1} light />
        <CtaInner>
          <CtaTitle>
            Book <em>Deck the Halls</em>
          </CtaTitle>
          <CtaBody>
            The Mice Trail is included with every Deck the Halls entry ticket. Book your Castle visit online — add Santa's Grotto, Festive Afternoon Tea or more to build your perfect Christmas package.
          </CtaBody>
          <CtaBtnRow>
            <CtaBtn href="/booking-journey?locationId=978&pageId=f7be8040-b3de-43e8-a064-b173b1a8d761">
              Book Now
            </CtaBtn>
            <CtaSecondary to="/christmas/your-festive-visit">
              Build Your Package
            </CtaSecondary>
          </CtaBtnRow>
          <CtaMeta>
            Christmas 2026 · Belvoir Castle · Mice Trail included with every Deck the Halls entry ticket · Family Saver available
          </CtaMeta>
        </CtaInner>
      </CtaSection>
    </Page>
    <Footer />
    </>
  );
};

export default MiceTrail;
